import React, { useState } from 'react'

import AutocompleteInput from 'components/Autocomplete'
import { useCompareBasket } from 'context/CompareBasketContext'
import { useEventContext } from 'context/EventContext'
import debounce from 'helpers/debounce'
import highlightWords, { escapeRegexSpecialCharacters } from 'helpers/highlightWords'

import classes from './Autocomplete.module.scss'

export const NO_SUGGESTION_TEXT = 'Make sure your keywords are spelled correctly or try another search.'
const MIN_INPUT_REQUIRED_LENGTH = 3
const SUGGESTIONS_EMPTY = []
const SUGGESTIONS_NOT_FOUND = [{ name: NO_SUGGESTION_TEXT, disabled: true }]
const API_COMPARE_SEARCH = '/compare/search'

const fetchListings = query =>
  fetch(`${API_COMPARE_SEARCH}?q=${encodeURIComponent(query)}`).then(response => response.json() || [])

const Autocomplete = ({ onClose }) => {
  const [suggestions, setSuggestions] = useState(SUGGESTIONS_EMPTY)
  const [latestSearch, setLatestSearch] = useState(null)
  const compareBasket = useCompareBasket()
  const { sendEvent } = useEventContext()

  const filterAlreadySelected = list => {
    const selectedSlugs = compareBasket.items.map(x => x.slug)
    return list.filter(({ slug }) => !selectedSlugs.includes(slug))
  }

  const fetchSuggestions = async inputValue => {
    if (latestSearch === inputValue) {
      return
    }
    try {
      const newSuggestions = filterAlreadySelected(await fetchListings(inputValue))
      setLatestSearch(inputValue)
      if (newSuggestions.length === 0) {
        setSuggestions(SUGGESTIONS_NOT_FOUND)
      } else {
        setSuggestions(newSuggestions)
      }
    } catch (err) {
      setSuggestions(SUGGESTIONS_NOT_FOUND)
    }
  }

  const handleInputChange = value => {
    const inputValue = value.trim()
    if (!inputValue) {
      setSuggestions(SUGGESTIONS_EMPTY)
    } else if (inputValue.length >= MIN_INPUT_REQUIRED_LENGTH) {
      fetchSuggestions(inputValue)
    }
  }

  const handleSuggestionClear = () => {
    setSuggestions(SUGGESTIONS_EMPTY)
    setLatestSearch(null)
    sendEvent({
      engagementComponent: 'compare-basket',
      engagementDestination: 'compare_search_erase-search-query',
      engagementDetails: 'search-bar',
      name: 'engagement_search',
    })
  }

  const handleSuggestionSelected = (event, suggestion) => {
    if (suggestion.disabled) return
    sendEvent({
      engagementComponent: 'compare-basket',
      engagementDestination: 'compare-basket_add-product',
      engagementDetails: 'search-bar',
      name: 'engagement_search',
    })
    compareBasket.addItem({
      id: suggestion.id,
      slug: suggestion.slug,
      name: suggestion.name,
      logo_url: suggestion.logo_url,
      cta_urls: suggestion.cta_urls,
    })
    onClose()
  }

  return (
    <AutocompleteInput
      className="Autocomplete"
      onChange={debounce(handleInputChange, 300)}
      componentsClasses={{
        paper: classes.suggestionPaperContainer,
        listbox: classes.suggestionContainer,
        option: classes.suggestionOption,
        popper: classes.popper,
      }}
      fullWidth
      options={suggestions}
      onClear={handleSuggestionClear}
      onSelect={handleSuggestionSelected}
      getLabel={option => option.name}
      inputProps={{
        placeholder: 'Search for an app',
        autoFocus: true,
        onFocus: () => {
          sendEvent({
            engagementComponent: 'compare-basket',
            engagementDestination: 'compare_search_select-search-bar',
            engagementDetails: 'search-bar',
            name: 'engagement_search',
          })
        },
      }}
      renderOption={(option, { inputValue }) => (
        <>
          {option.disabled ? (
            <div className="noResults">
              <div className={classes.noResultsTitle}>Sorry, we couldn’t find any matches.</div>
              <div className={classes.noResultsText}>
                Make sure your keywords are spelled correctly or try another search.
              </div>
            </div>
          ) : (
            <div className={classes.optionContainer}>
              {option.logo_url && <img src={option.logo_url} alt="app-logo" className={classes.logo} />}
              <span
                className={classes.suggestionText}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: highlightWords(option.name, escapeRegexSpecialCharacters(inputValue)),
                }}
              />
            </div>
          )}
        </>
      )}
      getOptionDisabled={option => option.disabled}
    />
  )
}

export default Autocomplete
